import React, { useCallback, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { NavigationProp, RouteProp, useFocusEffect, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { translate } from '../../../services/translate';
import { crmService } from '../../../services/central-api/crm';
import { GenericPaginatedParameters } from '../../../services/central-api/base-service';
import { CRMParamList } from '../../../typings/Params';
import { Pagination } from '../../../components/Pagination';
import MyDataTable from '../../../components/MyDataTable';
import DropdownButton from '../../../components/DropdownButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import FilterInput from '../../../components/Filter/components/FilterInput';
import DataTableContainer from '../../../components/DataTableContainer';

export default function CompaniesCameraConfiguration() {
    const navigation = useNavigation<NavigationProp<CRMParamList>>();
    const route = useRoute<RouteProp<CRMParamList, 'CompaniesCameraConfiguration'>>();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [configurations, setConfigurations] = useState<CompanyCameraConfiguration[]>([]);
    const [totalItems, setTotalItems] = useState(0);

    const [filters, setFilters] = useState<GenericPaginatedParameters>({
        textFilter: route.params.textFilter ?? '',
        page: Math.max(route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter);

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getCompaniesCameraConfiguration();
        }
    }, [isFocused, navigation, filters]));

    async function getCompaniesCameraConfiguration() {
        try {
            setIsLoading(true);
            const response = await crmService.getCompaniesCameraConfigurationPaginated(filters);
            setConfigurations(response.rows);
            setTotalItems(response.count);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    function handleFilters() {
        setFilters({
            ...filters,
            textFilter: textFilter !== '' ? textFilter : undefined,
            page: 0
        });

        navigation.setParams({
            textFilter: textFilter !== '' ? textFilter : undefined,
            page: 1
        });
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            handleFilters();
        }
    }

    return (
        <Container>
            <Header title={translate('companiesConfiguration')} />

            <Card>
                <DataTableHeader
                    title={translate('registeredConfigurations')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter ?? ''}
                        placeholder={translate('searchByCompanyName')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('company'),
                                selector: row => row.company.name,
                                grow: 3,
                                wrap: true,
                            },
                            {
                                name: translate('watermark'),
                                selector: row => row.overlayText,
                                grow: 2,
                                wrap: true,
                            },
                            {
                                name: translate('fps'),
                                selector: row => row.frameRate,
                            },
                            {
                                name: translate('resolution'),
                                selector: row => row.resolution,
                            },
                            {
                                button: true,
                                cell: row => <DropdownButton
                                    actions={[
                                        {
                                            content: translate('edit'),
                                            onPress: () => navigation.navigate('CompanyCameraConfigurationDetails', { companyId: row.companyId })
                                        }
                                    ]}
                                />
                            }
                        ]}
                        data={configurations}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={totalItems}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
        </Container>
    );
}
